import Skeleton from '@cobliteam/ui/components/Skeleton';
import Page from '@cobliteam/ui-business/components/layout/Page';

const ReplayHeaderSkeleton = () => (
  <header className='grid h-16 shrink-0 grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-4 overflow-hidden border-gray-4 border-b bg-white px-5 py-2'>
    <div className='flex items-center gap-2'>
      <Skeleton className='size-8 shrink-0 rounded-md bg-gray-3' />
      <div className='h-6 w-px shrink-0 bg-gray-4' />
    </div>
    <div className='flex min-w-0 items-center gap-2.5'>
      <Skeleton className='size-9 shrink-0 rounded-lg bg-gray-3' />
      <div className='flex min-w-0 flex-col gap-1.5'>
        <Skeleton className='h-5 w-28 bg-gray-4' />
        <Skeleton className='h-4 w-44 bg-gray-3' />
      </div>
    </div>
    <div className='flex flex-wrap items-center justify-end gap-3'>
      <Skeleton className='h-7 w-20 rounded-full bg-gray-3' />
      <Skeleton className='laptop:block hidden h-8 w-36 rounded-md bg-gray-3' />
      <Skeleton className='h-8 w-44 max-w-full rounded-md bg-gray-3' />
    </div>
  </header>
);

const SidebarTabsSkeleton = () => (
  <div className='flex h-12 shrink-0 items-stretch overflow-hidden border-gray-5 border-x border-b bg-white'>
    <div className='flex shrink-0 items-center border-gray-5 border-r px-2 py-2'>
      <div className='flex h-8 shrink-0 items-center gap-1 rounded-md bg-gray-2 px-2 ring-1 ring-gray-4 ring-inset'>
        <Skeleton className='size-5 shrink-0 rounded-md bg-gray-4' />
        <Skeleton className='h-3 w-8 bg-gray-4' />
      </div>
    </div>
    <div className='flex min-w-0 flex-1 items-center gap-1 overflow-hidden px-2 py-2'>
      <div className='flex h-8 shrink-0 items-center gap-1.5 rounded-md bg-gray-3 px-2.5'>
        <Skeleton className='size-4 rounded bg-gray-4' />
        <Skeleton className='h-3 w-20 bg-gray-4' />
      </div>
      {Array.from({ length: 5 }).map((_, index) => (
        <div key={index} className='flex h-8 shrink-0 items-center gap-1.5 rounded-md px-2.5'>
          <Skeleton className='size-4 rounded bg-gray-4' />
          <Skeleton className='h-3 w-16 bg-gray-3' />
          <Skeleton className='h-4 w-7 rounded-full bg-gray-3' />
        </div>
      ))}
    </div>
    <div className='flex shrink-0 items-center border-gray-5 border-l px-2 py-2'>
      <Skeleton className='size-8 rounded-md bg-gray-3' />
    </div>
  </div>
);

const OverviewPanelSkeleton = () => (
  <aside className='flex min-h-0 w-[336px] flex-1 shrink-0 flex-col overflow-hidden border-gray-4 border-r bg-gray-1'>
    <div className='border-gray-4 border-b bg-white px-4 py-3'>
      <Skeleton className='h-5 w-24 bg-gray-4' />
    </div>

    <div className='flex min-h-0 flex-1 flex-col gap-3 overflow-hidden p-3'>
      <div className='grid grid-cols-2 gap-2'>
        {Array.from({ length: 2 }).map((_, index) => (
          <div key={index} className='flex min-w-0 flex-col gap-2.5 rounded-lg border border-gray-4 bg-white p-3'>
            <Skeleton className='size-7 rounded-md bg-gray-3' />
            <div className='flex min-w-0 flex-col gap-1.5'>
              <Skeleton className='h-5 w-20 bg-gray-4' />
              <Skeleton className='h-4 w-24 bg-gray-3' />
            </div>
          </div>
        ))}
      </div>

      <div className='grid grid-cols-3 gap-2'>
        {Array.from({ length: 3 }).map((_, index) => (
          <div key={index} className='flex min-w-0 flex-col gap-2 rounded-lg border border-gray-4 bg-white px-2.5 py-2'>
            <div className='flex items-center justify-between gap-2'>
              <Skeleton className='size-6 rounded-md bg-gray-4' />
              <Skeleton className='h-4 w-6 bg-gray-4' />
            </div>
            <Skeleton className='h-4 w-16 bg-gray-3' />
          </div>
        ))}
      </div>

      <div className='flex min-h-0 flex-1 flex-col gap-2 overflow-hidden rounded-lg border border-gray-4 bg-white p-2'>
        {Array.from({ length: 6 }).map((_, index) => (
          <div key={index} className='flex items-center gap-2.5 rounded-lg px-2 py-2'>
            <Skeleton className='size-7 shrink-0 rounded-md bg-gray-3' />
            <div className='min-w-0 flex-1'>
              <Skeleton className='h-3 w-28 bg-gray-4' />
              <Skeleton className='mt-1 h-3 w-20 bg-gray-3' />
            </div>
            <Skeleton className='h-4 w-10 bg-gray-3' />
          </div>
        ))}
      </div>
    </div>
  </aside>
);

const ReplayMapSkeleton = () => (
  <main className='flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden bg-gray-2 p-3'>
    <div className='flex min-h-0 flex-1 flex-col overflow-hidden rounded-lg border border-gray-4 bg-white shadow-[0_1px_2px_rgba(0,0,0,0.04)]'>
      <div className='relative flex flex-1 overflow-hidden bg-gray-2'>
        <div className='absolute top-4 right-4 z-10 flex items-center gap-2'>
          <Skeleton className='h-8 w-20 rounded-md bg-gray-3' />
          <Skeleton className='h-8 w-9 rounded-md bg-gray-3' />
        </div>

        <div className='relative flex-1 overflow-hidden bg-gray-1'>
          <div className='absolute inset-0 grid grid-cols-8 grid-rows-6 gap-px bg-gray-3 p-px opacity-50'>
            {Array.from({ length: 48 }).map((_, index) => (
              <div key={index} className='bg-gray-1' />
            ))}
          </div>
        </div>
      </div>

      <div className='shrink-0 border-gray-4 border-t bg-white px-4 py-3'>
        <div className='flex items-center justify-between gap-4'>
          <div className='flex items-center gap-3'>
            <Skeleton className='h-8 w-28 rounded-md bg-gray-4' />
            <Skeleton className='h-8 w-24 rounded-md bg-gray-3' />
            <Skeleton className='h-6 w-32 rounded-full bg-gray-4' />
          </div>
          <Skeleton className='h-8 w-72 rounded-md bg-gray-3' />
        </div>
        <div className='mt-5 flex items-center gap-4'>
          <Skeleton className='size-9 rounded-full bg-gray-4' />
          <div className='relative h-2 flex-1 rounded-full bg-gray-4'>
            <Skeleton className='absolute inset-y-0 left-0 w-[52%] rounded-full bg-gray-5' />
            <Skeleton className='absolute top-1/2 left-[52%] size-5 -translate-y-1/2 rounded-full bg-gray-6' />
          </div>
        </div>
        <div className='mt-3 grid grid-cols-5 text-right'>
          {Array.from({ length: 5 }).map((_, index) => (
            <Skeleton key={index} className='ml-auto h-3 w-10 bg-gray-3' />
          ))}
        </div>
      </div>
    </div>
  </main>
);

export const TrackingReplayPageSkeleton = () => (
  <Page
    container={false}
    showFooter={false}
    showScrollToTop={false}
    className='!gap-0 !p-0 relative flex h-screen-without-header max-h-screen-without-header min-h-0 flex-1 flex-col overflow-hidden bg-gray-2'
  >
    <div className='mx-auto flex h-full max-h-full min-h-0 w-full max-w-[1440px] flex-col overflow-hidden border-gray-4 border-x bg-white shadow-sm'>
      <Page.Header className='block gap-0'>
        <ReplayHeaderSkeleton />
      </Page.Header>
      <Page.Tabs className='block h-12 shrink-0'>
        <SidebarTabsSkeleton />
      </Page.Tabs>

      <div className='flex h-[calc(100vh-var(--navbar-height)-4rem-3rem)] max-h-[calc(100vh-var(--navbar-height)-4rem-3rem)] min-h-0 flex-none flex-row gap-0 overflow-hidden bg-gray-2'>
        <div className='flex min-h-0 shrink-0 flex-col self-stretch overflow-hidden'>
          <OverviewPanelSkeleton />
        </div>
        <ReplayMapSkeleton />
      </div>
    </div>
  </Page>
);
